/**
 * NorseBuildings - Procedural Norse/Viking style buildings
 * 
 * Reusable building components for settlements:
 * - NorseHouse: Small/medium residential buildings
 * - NorseLonghouse: The Golden Mead Hall style large building
 * - BuildingProps: Barrels, crates, fences, etc.
 * 
 * All buildings use procedural geometry for lightweight rendering.
 */

import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { RigidBody, CuboidCollider } from '@react-three/rapier';
import * as THREE from 'three';

// Shared Thornhaven palette (Gothic / Victorian / Norse-fantasy) so decorative
// houses harmonise with the stone+timber+slate live buildings. Keep keys.
const WOOD_COLORS = {
  darkTimber: '#201309',
  mediumTimber: '#6a4428',
  lightTimber: '#8a6038',
  roof: '#2a2f39',
  roofAccent: '#1c2027',
  door: '#2c1d10',
  stone: '#767e89',
  stoneBase: '#464c56',
};

/**
 * Simple Norse House - Residential building
 */
export function NorseHouse({ 
  position = [0, 0, 0], 
  rotation = [0, 0, 0],
  scale = 1,
  variant = 0, // 0-2 for slight variations
}) {
  const width = (4 + variant * 0.5) * scale;
  const depth = (5 + variant * 0.3) * scale;
  const wallHeight = (2.5 + variant * 0.2) * scale;
  const roofHeight = (2 + variant * 0.1) * scale;
  
  const woodColor = variant === 0 ? WOOD_COLORS.mediumTimber : 
                    variant === 1 ? WOOD_COLORS.darkTimber : WOOD_COLORS.lightTimber;
  
  return (
    <group position={position} rotation={rotation}>
      {/* Collision */}
      <RigidBody type="fixed" colliders={false}>
        <CuboidCollider args={[width/2, (wallHeight + roofHeight)/2, depth/2]} position={[0, (wallHeight + roofHeight)/2, 0]} />
      </RigidBody>
      
      {/* Stone foundation */}
      <mesh position={[0, 0.15, 0]} castShadow receiveShadow>
        <boxGeometry args={[width + 0.3, 0.3, depth + 0.3]} />
        <meshStandardMaterial color={WOOD_COLORS.stoneBase} roughness={0.9} />
      </mesh>
      
      {/* Main walls */}
      <mesh position={[0, wallHeight/2 + 0.3, 0]} castShadow receiveShadow>
        <boxGeometry args={[width, wallHeight, depth]} />
        <meshStandardMaterial color={woodColor} roughness={0.8} />
      </mesh>
      
      {/* Horizontal timber beams */}
      {[-1, 0, 1].map((i) => (
        <mesh key={`beam-${i}`} position={[0, 0.8 + i * 0.8, depth/2 + 0.02]} castShadow>
          <boxGeometry args={[width + 0.1, 0.1, 0.08]} />
          <meshStandardMaterial color={WOOD_COLORS.darkTimber} roughness={0.7} />
        </mesh>
      ))}
      
      {/* Roof - pitched */}
      <group position={[0, wallHeight + 0.3, 0]}>
        {/* Left roof slope */}
        <mesh position={[-width/4, roofHeight/2 - 0.2, 0]} rotation={[0, 0, Math.PI/4]} castShadow receiveShadow>
          <boxGeometry args={[width/1.4, 0.15, depth + 0.4]} />
          <meshStandardMaterial color={WOOD_COLORS.roof} roughness={0.9} />
        </mesh>
        {/* Right roof slope */}
        <mesh position={[width/4, roofHeight/2 - 0.2, 0]} rotation={[0, 0, -Math.PI/4]} castShadow receiveShadow>
          <boxGeometry args={[width/1.4, 0.15, depth + 0.4]} />
          <meshStandardMaterial color={WOOD_COLORS.roof} roughness={0.9} />
        </mesh>
        {/* Roof ridge */}
        <mesh position={[0, roofHeight - 0.3, 0]} castShadow>
          <boxGeometry args={[0.2, 0.15, depth + 0.6]} />
          <meshStandardMaterial color={WOOD_COLORS.roofAccent} roughness={0.8} />
        </mesh>
      </group>
      
      {/* Door */}
      <mesh position={[0, 1, depth/2 + 0.02]} castShadow>
        <boxGeometry args={[0.9, 1.8, 0.08]} />
        <meshStandardMaterial color={WOOD_COLORS.door} roughness={0.7} />
      </mesh>
      
      {/* Door frame */}
      <mesh position={[0, 1, depth/2 + 0.04]} castShadow>
        <boxGeometry args={[1.1, 2, 0.04]} />
        <meshStandardMaterial color={WOOD_COLORS.darkTimber} roughness={0.7} />
      </mesh>
      
      {/* Window (small) */}
      <mesh position={[width/3, 1.5, depth/2 + 0.02]} castShadow>
        <boxGeometry args={[0.5, 0.5, 0.06]} />
        <meshStandardMaterial color="#2A2A2A" />
      </mesh>
    </group>
  );
}

/**
 * Norse Longhouse - Large building like the Golden Mead Hall
 */
export function NorseLonghouse({ 
  position = [0, 0, 0], 
  rotation = [0, 0, 0],
  isGoldenMeadHall = false,
  entrancePosition = { x: 0, z: 8 }, // For interaction alignment
}) {
  const width = 10;
  const depth = 16;
  const wallHeight = 4;
  const roofHeight = 3;
  
  return (
    <group position={position} rotation={rotation}>
      {/* Collision - main building */}
      <RigidBody type="fixed" colliders={false}>
        <CuboidCollider args={[width/2, (wallHeight + roofHeight)/2, depth/2]} position={[0, (wallHeight + roofHeight)/2, 0]} />
      </RigidBody>
      
      {/* Stone foundation - larger and more prominent */}
      <mesh position={[0, 0.25, 0]} receiveShadow>
        <boxGeometry args={[width + 1, 0.5, depth + 1]} />
        <meshStandardMaterial color={WOOD_COLORS.stone} roughness={0.95} />
      </mesh>
      
      {/* Main timber walls */}
      <mesh position={[0, wallHeight/2 + 0.5, 0]} castShadow receiveShadow>
        <boxGeometry args={[width, wallHeight, depth]} />
        <meshStandardMaterial 
          color={isGoldenMeadHall ? '#6D4C41' : WOOD_COLORS.mediumTimber} 
          roughness={0.75} 
        />
      </mesh>
      
      {/* Decorative timber frame - horizontal beams */}
      {[0.5, 1.5, 2.5, 3.5].map((h) => (
        <group key={`hbeam-${h}`}>
          {/* Front */}
          <mesh position={[0, h + 0.5, depth/2 + 0.03]} castShadow>
            <boxGeometry args={[width + 0.2, 0.12, 0.1]} />
            <meshStandardMaterial color={WOOD_COLORS.darkTimber} roughness={0.7} />
          </mesh>
          {/* Back */}
          <mesh position={[0, h + 0.5, -depth/2 - 0.03]} castShadow>
            <boxGeometry args={[width + 0.2, 0.12, 0.1]} />
            <meshStandardMaterial color={WOOD_COLORS.darkTimber} roughness={0.7} />
          </mesh>
        </group>
      ))}
      
      {/* Vertical timber posts */}
      {[-width/2 + 0.5, -width/4, 0, width/4, width/2 - 0.5].map((x, i) => (
        <mesh key={`vpost-${i}`} position={[x, wallHeight/2 + 0.5, depth/2 + 0.03]} castShadow>
          <boxGeometry args={[0.15, wallHeight, 0.1]} />
          <meshStandardMaterial color={WOOD_COLORS.darkTimber} roughness={0.7} />
        </mesh>
      ))}
      
      {/* Large pitched roof */}
      <group position={[0, wallHeight + 0.5, 0]}>
        {/* Left roof slope */}
        <mesh position={[-width/3.5, roofHeight/2.5, 0]} rotation={[0, 0, 0.55]} castShadow receiveShadow>
          <boxGeometry args={[width/1.6, 0.25, depth + 1.5]} />
          <meshStandardMaterial color={isGoldenMeadHall ? '#3E2723' : WOOD_COLORS.roof} roughness={0.85} />
        </mesh>
        {/* Right roof slope */}
        <mesh position={[width/3.5, roofHeight/2.5, 0]} rotation={[0, 0, -0.55]} castShadow receiveShadow>
          <boxGeometry args={[width/1.6, 0.25, depth + 1.5]} />
          <meshStandardMaterial color={isGoldenMeadHall ? '#3E2723' : WOOD_COLORS.roof} roughness={0.85} />
        </mesh>
        
        {/* Roof ridge beam */}
        <mesh position={[0, roofHeight - 0.2, 0]} castShadow>
          <boxGeometry args={[0.35, 0.25, depth + 2]} />
          <meshStandardMaterial color={WOOD_COLORS.roofAccent} roughness={0.75} />
        </mesh>
        
        {/* Decorative roof ends */}
        {[depth/2 + 0.8, -depth/2 - 0.8].map((z, i) => (
          <group key={`roofend-${i}`} position={[0, roofHeight - 0.5, z]}>
            {/* Crossed beams (simplified) */}
            <mesh rotation={[0, 0, 0.3]} castShadow>
              <boxGeometry args={[0.12, 1.2, 0.12]} />
              <meshStandardMaterial color={WOOD_COLORS.darkTimber} />
            </mesh>
            <mesh rotation={[0, 0, -0.3]} castShadow>
              <boxGeometry args={[0.12, 1.2, 0.12]} />
              <meshStandardMaterial color={WOOD_COLORS.darkTimber} />
            </mesh>
          </group>
        ))}
      </group>
      
      {/* Main entrance - large decorated doorway */}
      <group position={[0, 0, depth/2]}>
        {/* Door opening (dark) */}
        <mesh position={[0, 1.6, 0.02]}>
          <boxGeometry args={[1.8, 2.8, 0.1]} />
          <meshStandardMaterial color="#0A0A0A" />
        </mesh>
        
        {/* Door frame - ornate */}
        <mesh position={[0, 1.6, 0.08]} castShadow>
          <boxGeometry args={[2.2, 3.2, 0.12]} />
          <meshStandardMaterial color={WOOD_COLORS.darkTimber} roughness={0.6} />
        </mesh>
        
        {/* Decorative arch top */}
        <mesh position={[0, 3.3, 0.1]} castShadow>
          <boxGeometry args={[2.4, 0.3, 0.15]} />
          <meshStandardMaterial color={isGoldenMeadHall ? '#8D6E63' : WOOD_COLORS.mediumTimber} roughness={0.6} />
        </mesh>
        
        {/* Side pillars */}
        {[-1.3, 1.3].map((x, i) => (
          <mesh key={`pillar-${i}`} position={[x, 1.7, 0.15]} castShadow>
            <boxGeometry args={[0.25, 3.4, 0.25]} />
            <meshStandardMaterial color={WOOD_COLORS.darkTimber} roughness={0.7} />
          </mesh>
        ))}
        
        {/* Torch holders */}
        {[-1.8, 1.8].map((x, i) => (
          <group key={`torch-${i}`} position={[x, 2.5, 0.3]}>
            <mesh castShadow>
              <cylinderGeometry args={[0.08, 0.05, 0.4, 8]} />
              <meshStandardMaterial color={WOOD_COLORS.darkTimber} />
            </mesh>
            {/* Flame */}
            <mesh position={[0, 0.3, 0]}>
              <coneGeometry args={[0.08, 0.25, 8]} />
              <meshBasicMaterial color="#FF6600" />
            </mesh>
          </group>
        ))}
      </group>
      
      {/* Sign above door (for Golden Mead Hall) */}
      {isGoldenMeadHall && (
        <group position={[0, 4.2, depth/2 + 0.3]}>
          <mesh castShadow>
            <boxGeometry args={[3, 0.6, 0.1]} />
            <meshStandardMaterial color={WOOD_COLORS.darkTimber} />
          </mesh>
          {/* Gold accent */}
          <mesh position={[0, 0, 0.06]}>
            <boxGeometry args={[2.6, 0.4, 0.02]} />
            <meshStandardMaterial color="#C9A227" metalness={0.7} roughness={0.4} />
          </mesh>
        </group>
      )}
    </group>
  );
}

/**
 * Barrel - Reusable prop
 */
export function Barrel({ position = [0, 0, 0], rotation = [0, 0, 0], scale = 1 }) {
  return (
    <group position={position} rotation={rotation}>
      <mesh position={[0, 0.4 * scale, 0]} castShadow>
        <cylinderGeometry args={[0.35 * scale, 0.3 * scale, 0.8 * scale, 12]} />
        <meshStandardMaterial color="#5D4037" roughness={0.8} />
      </mesh>
      {/* Metal bands */}
      {[0.15, 0.45, 0.65].map((h, i) => (
        <mesh key={i} position={[0, h * scale, 0]} castShadow>
          <torusGeometry args={[0.33 * scale, 0.025 * scale, 8, 16]} rotation={[Math.PI/2, 0, 0]} />
          <meshStandardMaterial color="#4A4A4A" metalness={0.6} roughness={0.5} />
        </mesh>
      ))}
    </group>
  );
}

/**
 * Crate - Reusable prop
 */
export function Crate({ position = [0, 0, 0], rotation = [0, 0, 0], scale = 1 }) {
  const size = 0.6 * scale;
  return (
    <group position={position} rotation={rotation}>
      <mesh position={[0, size/2, 0]} castShadow>
        <boxGeometry args={[size, size, size]} />
        <meshStandardMaterial color="#6D4C41" roughness={0.85} />
      </mesh>
      {/* Slats */}
      {[-0.2, 0, 0.2].map((offset, i) => (
        <mesh key={i} position={[offset * scale, size/2, size/2 + 0.01]} castShadow>
          <boxGeometry args={[0.08 * scale, size - 0.05, 0.02]} />
          <meshStandardMaterial color="#4E342E" roughness={0.8} />
        </mesh>
      ))}
    </group>
  );
}

/**
 * Fence Section - Reusable wooden fence
 */
export function FenceSection({ position = [0, 0, 0], rotation = [0, 0, 0], length = 3 }) {
  const postCount = Math.ceil(length / 1.5) + 1;
  
  return (
    <group position={position} rotation={rotation}>
      {/* Collision */}
      <RigidBody type="fixed" colliders={false}>
        <CuboidCollider args={[length/2, 0.5, 0.1]} position={[0, 0.5, 0]} />
      </RigidBody>
      
      {/* Posts */}
      {Array.from({ length: postCount }).map((_, i) => {
        const x = -length/2 + (i * length / (postCount - 1));
        return (
          <mesh key={`post-${i}`} position={[x, 0.5, 0]} castShadow>
            <boxGeometry args={[0.1, 1, 0.1]} />
            <meshStandardMaterial color={WOOD_COLORS.darkTimber} roughness={0.85} />
          </mesh>
        );
      })}
      
      {/* Horizontal rails */}
      <mesh position={[0, 0.3, 0]} castShadow>
        <boxGeometry args={[length, 0.08, 0.06]} />
        <meshStandardMaterial color={WOOD_COLORS.mediumTimber} roughness={0.8} />
      </mesh>
      <mesh position={[0, 0.7, 0]} castShadow>
        <boxGeometry args={[length, 0.08, 0.06]} />
        <meshStandardMaterial color={WOOD_COLORS.mediumTimber} roughness={0.8} />
      </mesh>
    </group>
  );
}

/**
 * Simple Tree - Low-poly stylized tree
 */
export function Tree({ position = [0, 0, 0], scale = 1, variant = 0 }) {
  const trunkHeight = (2 + variant * 0.5) * scale;
  const foliageRadius = (1.5 + variant * 0.3) * scale;
  const foliageColor = variant === 0 ? '#2D5016' : variant === 1 ? '#3D6B22' : '#1E4010';
  
  return (
    <group position={position}>
      {/* Trunk */}
      <mesh position={[0, trunkHeight/2, 0]} castShadow>
        <cylinderGeometry args={[0.15 * scale, 0.25 * scale, trunkHeight, 8]} />
        <meshStandardMaterial color="#4A3728" roughness={0.9} />
      </mesh>
      {/* Foliage - layered cones */}
      <mesh position={[0, trunkHeight + foliageRadius * 0.3, 0]} castShadow>
        <coneGeometry args={[foliageRadius, foliageRadius * 1.5, 8]} />
        <meshStandardMaterial color={foliageColor} roughness={0.85} />
      </mesh>
      <mesh position={[0, trunkHeight + foliageRadius * 0.9, 0]} castShadow>
        <coneGeometry args={[foliageRadius * 0.7, foliageRadius * 1.2, 8]} />
        <meshStandardMaterial color={foliageColor} roughness={0.85} />
      </mesh>
    </group>
  );
}

/**
 * Rock - Simple rock prop
 */
export function Rock({ position = [0, 0, 0], scale = 1, variant = 0 }) {
  return (
    <mesh position={[position[0], position[1] + 0.2 * scale, position[2]]} castShadow>
      <dodecahedronGeometry args={[0.4 * scale, 0]} />
      <meshStandardMaterial 
        color={variant === 0 ? '#6B6B6B' : variant === 1 ? '#7D7D7D' : '#5A5A5A'} 
        roughness={0.95} 
        flatShading 
      />
    </mesh>
  );
}
